import { useEffect, useState } from "react";
import {
  useListMemberCharges, getListMemberChargesQueryKey,
  useUpdateMemberCharge,
  type MemberChargeStatus, type ListMemberChargesParams, type MemberChargeListResponse,
} from "@workspace/api-client-react";
import { ListPagination } from "@/components/common/list-pagination";
import { useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Pencil, XCircle, Flag, LockOpen } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/auth-context";
import { BillingStatusBadge, type BillingStatus } from "./billing-status-badge";
import { BillingSourceBadge } from "./billing-source-badge";
import { PlanCoverageAlert } from "./plan-coverage-alert";
import { WaiveChargeDialog } from "./waive-charge-dialog";

type Chip = "All" | "Pending" | "Disputed";

export function ChargesInbox() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [chip, setChip] = useState<Chip>("Pending");
  const [memberSearch, setMemberSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [waiveId, setWaiveId] = useState<number | null>(null);

  const trimmedSearch = memberSearch.trim();
  const memberIdParam =
    trimmedSearch !== "" && Number.isFinite(Number(trimmedSearch)) ? Number(trimmedSearch) : undefined;
  const params: ListMemberChargesParams = {
    ...(chip === "All" ? {} : { status: chip as MemberChargeStatus }),
    ...(memberIdParam !== undefined ? { memberId: memberIdParam } : {}),
    page,
    pageSize,
  };
  const { data: chargePage, isLoading } = useListMemberCharges(params, {
    query: { queryKey: getListMemberChargesQueryKey(params) },
  });
  const charges = chargePage?.items ?? [];
  const total = chargePage?.total ?? 0;

  // Reset to page 1 when filters change.
  useEffect(() => {
    setPage(1);
  }, [chip, memberSearch, pageSize]);

  const updateMutation = useUpdateMemberCharge({
    mutation: {
      onMutate: async (vars) => {
        // Optimistic update for dispute toggle (UI-SPEC §Pattern 4)
        await qc.cancelQueries({ queryKey: getListMemberChargesQueryKey() });
        const prev = qc.getQueryData(getListMemberChargesQueryKey(params));
        qc.setQueryData(getListMemberChargesQueryKey(params), (old: unknown) => {
          const env = old as MemberChargeListResponse | undefined;
          if (!env?.items) return old;
          return {
            ...env,
            items: env.items.map((c) =>
              c.id === vars.id ? { ...c, status: vars.data.status ?? c.status } : c,
            ),
          };
        });
        return { prev };
      },
      onError: (_err, _vars, ctx) => {
        qc.setQueryData(getListMemberChargesQueryKey(params), (ctx as { prev: unknown }).prev);
        toast.error("Update failed; reverted");
      },
      onSettled: () => qc.invalidateQueries({ queryKey: getListMemberChargesQueryKey() }),
    },
  });

  return (
    <div className="space-y-4">
      <PlanCoverageAlert variant="panel" />

      <div className="flex items-center gap-2">
        {(["All", "Pending", "Disputed"] as Chip[]).map((c) => (
          <Button key={c} size="sm" variant={chip === c ? "default" : "outline"} onClick={() => setChip(c)}>{c}</Button>
        ))}
        <Input
          placeholder="Search member id…"
          className="ml-auto max-w-xs"
          value={memberSearch}
          onChange={(e) => setMemberSearch(e.target.value)}
        />
      </div>

      {isLoading ? <Skeleton className="h-64 w-full" /> : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Charge ID</TableHead>
                <TableHead>Member</TableHead>
                <TableHead>Item</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Flags</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {charges.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-mono">CH-{String(c.id).padStart(4, "0")}</TableCell>
                  <TableCell>MEM-{String(c.memberId).padStart(4, "0")}</TableCell>
                  <TableCell className="text-xs">{c.description}</TableCell>
                  <TableCell><BillingSourceBadge sourceType={c.sourceType} /></TableCell>
                  <TableCell className="font-mono text-right">Rs. {Number(c.total).toFixed(2)}</TableCell>
                  <TableCell className="text-xs">{new Date(c.transactionDate).toLocaleDateString("en-IN")}</TableCell>
                  <TableCell><BillingStatusBadge status={c.status as BillingStatus} /></TableCell>
                  <TableCell>
                    {c.status === "Disputed" ? <LockOpen className="h-4 w-4 text-orange-500" /> : null}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" aria-label="Edit Charge" disabled={c.status === "Billed" || c.status === "Paid" || c.status === "Waived"}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <Button size="icon" variant="ghost" aria-label="Waive Charge"
                                disabled={!isAdmin || c.status === "Waived" || c.status === "Paid"}
                                onClick={() => setWaiveId(c.id)}>
                                <XCircle className="h-4 w-4 text-red-500" />
                              </Button>
                            </span>
                          </TooltipTrigger>
                          {!isAdmin ? <TooltipContent>Admin only — contact your administrator.</TooltipContent> : null}
                        </Tooltip>
                      </TooltipProvider>
                      <Button size="icon" variant="ghost" aria-label={c.status === "Disputed" ? "Unflag Dispute" : "Flag Dispute"}
                        disabled={c.status === "Billed" || c.status === "Paid" || c.status === "Waived"}
                        onClick={() => updateMutation.mutate({
                          id: c.id,
                          data: { status: c.status === "Disputed" ? "Pending" : "Disputed" },
                        })}>
                        <Flag className="h-4 w-4 text-orange-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {charges.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">No charges match your filters.</TableCell></TableRow>
              ) : null}
            </TableBody>
          </Table>
          {total > 0 ? (
            <div className="px-4">
              <ListPagination
                total={total}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                isLoading={isLoading}
                noun={["charge", "charges"]}
              />
            </div>
          ) : null}
        </Card>
      )}

      <WaiveChargeDialog chargeId={waiveId} onClose={() => setWaiveId(null)} />
    </div>
  );
}
